<script lang="ts">
  import { CarouselButtonGroup, CarouselProvider, CarouselStage, Text } from '@squareup/public-web-design-system';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { createLazyLoadGroup } from '$lib/actions/create-lazy-load-group';
  import type { PwNextCarouselProps } from './types';

  const {
    entryId,
    backgroundColor,
    customCssClass,
    heading,
    headingLevel,
    headingSize,
    slides,
    slidesToShow,
    slidesToShowOnMobile,
    columnsOnMobile,
    // https://www.embla-carousel.com/api/options/
    carouselOptions,
    buttonAlignment,
    flexChildren,
  }: PwNextCarouselProps = $props();

  const slideElements: HTMLElement[] = [];

  function handleSlideChange({ currentIndex }: { currentIndex: number }): void {
    const currentSlide = slideElements[currentIndex];
    const text = currentSlide.innerText;

    window.Sq.Tracking.track(
      {
        action: 'Slide View',
        category: 'Carousel',
        properties: {
          index: currentIndex,
          text,
        },
      },
      {
        destinations: ['googleAnalytics'],
      }
    );

    window.Sq.Tracking.track(
      {
        action: 'Advance Carousel',
        properties: {
          slideIndex: currentIndex,
          slideText: text,
        },
      },
      {
        destinations: ['cdp'],
      }
    );
  }

  const alignItemsMap: Record<PwNextCarouselProps['buttonAlignment'], string> = {
    left: 'flex-start',
    right: 'flex-end',
    center: 'center',
  };
</script>

<section
  class="PwNextCarousel PwNextCarousel-{entryId}-box-properties {customCssClass}"
  class:PwNextCarousel--fullscreen-loop={slidesToShow === 1 && carouselOptions.loop}
  class:PwNextCarousel--no-heading={!heading}
  class:PwNextCarousel--flex-children={flexChildren}
  data-entry-id={entryId}
  style:--pw-next-carousel-align-items={alignItemsMap[buttonAlignment]}
  use:createLazyLoadGroup
>
  <CarouselProvider>
    {#if heading}
      <div class="PwNextCarousel__header">
        <Text as={headingLevel} variant={headingSize}>
          {heading}
        </Text>

        <!-- If there is a heading, then the buttons always appear opposite of the heading -->
        <CarouselButtonGroup parentBackgroundColor={backgroundColor} />
      </div>
    {/if}

    <CarouselStage
      {slides}
      {slidesToShow}
      {slidesToShowOnMobile}
      {columnsOnMobile}
      options={carouselOptions}
      onSlideChange={handleSlideChange}
      {slideElements}
    >
      {#snippet slide({ slide })}
        <DynamicChildrenComponent children={[slide]} />
      {/snippet}
    </CarouselStage>

    <!-- The buttons are always underneath on smaller screen sizes, so have this element already on the DOM -->
    <CarouselButtonGroup parentBackgroundColor={backgroundColor} />
  </CarouselProvider>
</section>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;

  @layer custom-components {
    .PwNextCarousel {
      // There are two different CarouselButtonGroups, but there was a bug where if a heading was not used then neither
      // one would be displayed on medium and above screen sizes
      &:not(.PwNextCarousel--no-heading) {
        :global {
          & > .CarouselButtonGroup {
            @include media.at('medium') {
              display: none;
            }
          }
        }
      }

      // https://github.com/davidjerleke/embla-carousel/issues/118#issuecomment-729158683
      &.PwNextCarousel--fullscreen-loop {
        :global {
          .CarouselStage {
            &__stage {
              width: 100%;
            }

            &__slide {
              max-width: var(--container-width-current);
            }
          }
        }
      }

      &__header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: var(--space-60);
        width: var(--container-width-current);
        margin: {
          right: var(--space-auto);
          left: var(--space-auto);
        }
        padding-bottom: var(--space-60);

        @include media.at('medium') {
          padding-top: var(--space-80);
        }

        :global {
          .CarouselButtonGroup {
            @include media.until('medium') {
              display: none;
            }
          }
        }
      }

      :global {
        & > .CarouselButtonGroup {
          justify-content: var(--pw-next-carousel-align-items);
          width: var(--container-width-current);
          margin: {
            right: var(--space-auto);
            left: var(--space-auto);
          }
          padding-top: var(--space-60);
        }
      }

      // Defaults to flexing children but allows for it to be turned off.
      &--flex-children {
        :global {
          .CarouselStage__slide {
            &,
            & > * {
              display: flex;
            }
          }
        }
      }
    }
  }
</style>
